import { useState } from "react";
import { Button } from "../../components/Button";
import { BackButton } from "../../components/BackButton";
import { Field } from "../../components/Field";
import { PhoneInput } from "../../components/PhoneInput";
import { ALL_ACCESS_MINOR, naira } from "../../lib/format";
import { DEFAULT_COUNTRY, normalisePhone, phoneError, type CountryDialInfo } from "../../lib/phone";
import { shareUrl, type ShareChannel } from "../../lib/share";

interface AskParentScreenProps {
  /** A preview of what we will send. Written by the server, not editable. */
  preview?: string | undefined;
  sending?: boolean | undefined;
  /** Set once it has gone, so the screen can say where. */
  sentTo?: string | null | undefined;
  error?: string | null | undefined;
  /**
   * For email: server sends. For SMS/WhatsApp: returns the shareable link so
   * the frontend can open the native app with the full message.
   */
  onSend?:
    | ((input: { channel: ShareChannel; contact: string }) => Promise<{ link: string } | void>)
    | undefined;
  onDone?: (() => void) | undefined;
}

const CHANNELS: { id: ShareChannel; label: string; verb: string }[] = [
  { id: "sms", label: "Text", verb: "Text my parent" },
  { id: "whatsapp", label: "WhatsApp", verb: "Message my parent" },
  { id: "email", label: "Email", verb: "Email my parent" },
];

/**
 * Screen 13b — asking a parent to pay.
 *
 * The wall's second action had nowhere to go: it navigated to `/p/pay`, which
 * is the *parent's* page and needs a payment reference, so it fell through to
 * the not-found screen. For an under-18 this is the conversion path, not a
 * secondary one — a student who cannot pay themselves had no route at all.
 *
 * We send it, rather than handing the student off to their messaging app.
 * Same three channels as the consent flow and the same reason: a parent who
 * does not read texts is a payment that never happens, and the student is the
 * one who knows which it is.
 *
 * The message is shown but not editable. The student chooses who to reach and
 * how; they do not choose the words, because text supplied by a client and
 * delivered to a number a client chose is an open relay with our name on it.
 */
export function AskParentScreen({
  preview,
  sending = false,
  sentTo = null,
  error = null,
  onSend,
  onDone,
}: AskParentScreenProps) {
  const [channel, setChannel] = useState<ShareChannel>("sms");
  const [country, setCountry] = useState<CountryDialInfo>(DEFAULT_COUNTRY);
  const [digits, setDigits] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const byEmail = channel === "email";
  const normalised = normalisePhone(digits, country);
  const trimmedEmail = email.trim().toLowerCase();

  const problem = byEmail
    ? !trimmedEmail
      ? "Enter your parent's email address."
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)
        ? "That doesn't look like an email address."
        : null
    : phoneError(digits, country);

  const shown = error ?? (submitted ? problem : null);
  const action = CHANNELS.find((option) => option.id === channel)?.verb ?? "Send";

  const submit = async () => {
    setSubmitted(true);
    if (problem !== null || sending) return;

    /*
     * For all channels, call onSend first — the backend creates the checkout
     * and returns the payment link. For email, the backend also sends the
     * message. For SMS/WhatsApp, the frontend opens the device's native app
     * with the full message + payment link pre-filled.
     */
    const result = await onSend?.({
      channel,
      contact: byEmail ? trimmedEmail : (normalised ?? ""),
    });

    if (channel !== "email" && result?.link && preview) {
      const fullMessage = `${preview}\n\n${result.link}`;
      const url = shareUrl({
        channel,
        message: fullMessage,
        subject: "",
        recipient: normalised ?? undefined,
      });
      window.open(url, "_self");
    }
  };

  if (sentTo) {
    return (
      <div className="app-column px-5 pb-6 pt-6">
        <BackButton to="/upgrade" />
        <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.2]">
          Sent to your parent
        </h1>
        <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
          We sent the payment link to <span className="text-text">{sentTo}</span>. Everything
          unlocks the moment they pay — you don't need to do anything else.
        </p>
        <div className="mt-auto pt-8">
          <Button onClick={onDone}>Keep practicing meanwhile</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/upgrade" />

      <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.2]">
        Ask a parent to pay
      </h1>
      <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
        We'll send them the link. They pay {naira(ALL_ACCESS_MINOR)} once, with no account to
        create, and everything unlocks the moment it clears.
      </p>

      {/*
        The student picks, because they know which one their parent reads.
        Same three as the consent flow, deliberately.
      */}
      <div
        role="group"
        aria-label="How to reach them"
        className="mt-6 flex gap-1 rounded-[var(--radius-chip)] border border-border bg-surface p-1"
      >
        {CHANNELS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={channel === option.id}
            disabled={sending}
            onClick={() => {
              setChannel(option.id);
              setSubmitted(false);
            }}
            className={`min-h-10 flex-1 rounded-[var(--radius-chip)] px-3 text-[12.5px] font-semibold transition-colors disabled:opacity-40 ${
              channel === option.id ? "bg-accent-tint text-text" : "text-text-muted"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="pt-4">
        {byEmail ? (
          <Field
            label="Parent's email address"
            htmlFor="parent-pay-email"
            error={shown}
            hint="Use the address they actually check."
          >
            <input
              id="parent-pay-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(shown)}
              placeholder="parent@example.com"
              className={`min-h-12 w-full rounded-[var(--radius-control)] border bg-surface px-3.5 text-[15px] text-text outline-none placeholder:text-text-muted focus:border-accent ${
                shown ? "border-negative" : "border-border-strong"
              }`}
            />
          </Field>
        ) : (
          <Field
            label={channel === "whatsapp" ? "Parent's WhatsApp number" : "Parent's phone number"}
            htmlFor="parent-pay-phone"
            error={shown}
            hint={
              channel === "whatsapp"
                ? "Use the number they have WhatsApp on."
                : "Use the number they read texts on."
            }
          >
            <PhoneInput
              id="parent-pay-phone"
              value={digits}
              onChange={setDigits}
              country={country}
              onCountryChange={setCountry}
              invalid={Boolean(shown)}
            />
          </Field>
        )}
      </div>

      {/* Showing the message is a trust move: the student can see nothing
          embarrassing goes out on their behalf, and it carries their own
          scores. */}
      {preview ? (
        <div className="mt-5 flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
            What they'll get
          </p>
          <p className="m-0 whitespace-pre-line text-[13px] leading-[1.55]">{preview}</p>
        </div>
      ) : null}

      <div className="mt-auto pt-8">
        <Button disabled={sending} onClick={submit}>
          {sending ? "Sending…" : action}
        </Button>
      </div>

      <p className="m-0 pt-4 text-center text-[12px] leading-[1.5] text-text-muted">
        Your score and streak stay safe either way.
      </p>
    </div>
  );
}
